import {DialogActions, DialogContent, Modal, ModalClose, ModalDialog, Stack, Typography} from "@mui/joy"
import {Check, Hourglass, X} from "lucide-react"
import {useTranslation} from "react-i18next"
import Button from "./Button"
import {TransactionStatus} from "../types/transaction"

/** What the client is shown while it waits, and once it stops waiting */
export type PaymentDialogStatus = TransactionStatus | "timeout"

export type PaymentStatusDialogProps = {
    open: boolean
    status?: PaymentDialogStatus
    statusLoading?: boolean
    onCheckStatus?: () => void
    onClose: () => void
    /** Where a confirmed payment sends the visitor next */
    onDone?: () => void
}

const COPY: { [status in PaymentDialogStatus]?: { title: string, description: string } } = {
    pending: {title: "paymentPendingTitle", description: "paymentPendingDescription"},
    successful: {title: "paymentSuccessTitle", description: "paymentSuccessDescription"},
    failed: {title: "paymentFailedTitle", description: "paymentFailedDescription"},
    cancelled: {title: "paymentCancelledTitle", description: "paymentCancelledDescription"},
    refunded: {title: "paymentFailedTitle", description: "paymentFailedDescription"},
    timeout: {title: "paymentTimeoutTitle", description: "paymentTimeoutDescription"}
}

const StatusIcon = ({status}: { status?: PaymentDialogStatus }) => {
    const common = {size: 34}

    if (status === "successful")
        return <Check {...common} color={"#2DDF82"}/>

    if (status === "pending" || status === "timeout")
        return <Hourglass {...common}/>

    return <X {...common} color={"#e5484d"}/>
}

/**
 * Where a mobile money payment is followed.
 *
 * The provider answers long after the request, so the outcome arrives on its own — pushed over the
 * socket the checkout opened. The status button is what is left when it never does.
 */
const PaymentStatusDialog = (props: PaymentStatusDialogProps) => {
    const {t} = useTranslation()
    const copy = COPY[props.status] || COPY.pending
    const waiting = props.status === "pending" || props.status === "timeout"

    return <Modal open={props.open} onClose={(event, reason) => reason === "closeClick" && props.onClose()}>
        <ModalDialog sx={{
            width: {xs: "calc(100vw - 32px)", sm: "26rem"},
            maxWidth: "calc(100vw - 32px)",
            boxSizing: "border-box",
            gap: 2,
            borderRadius: 15,
            backgroundColor: "#0a1626",
            borderColor: "rgba(255,255,255,0.1)"
        }}>
            <ModalClose aria-label={t("close")}/>

            <DialogContent sx={{alignItems: "center", gap: 2, textAlign: "center"}}>
                <StatusIcon status={props.status}/>
                <Stack gap={1}>
                    <Typography level={"h3"}>{t(copy.title)}</Typography>
                    <Typography level={"body-sm"}>{t(copy.description)}</Typography>
                </Stack>
            </DialogContent>

            <DialogActions sx={{justifyContent: "center"}}>
                {waiting
                    ? <Stack gap={0.5} alignItems={"center"}>
                        <Button loading={props.statusLoading} onClick={props.onCheckStatus}>
                            {t("checkPaymentStatus")}
                        </Button>
                        <Typography level={"body-xs"}>{t("paymentProcessingNotice")}</Typography>
                    </Stack>
                    : <Button variant={"soft"} color={"neutral"}
                              onClick={props.status === "successful" ? props.onDone : props.onClose}>
                        {props.status === "successful" ? t("startAnalyzing") : t("close")}
                    </Button>}
            </DialogActions>
        </ModalDialog>
    </Modal>
}

export default PaymentStatusDialog
